/**
 * Audit-log query hooks (Phase 13 Cluster A — Feature D).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import type { AuditAction, AuditEntityType, AuditListQuery } from '@armard/shared';

export interface AuditListEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: AuditAction;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  timestamp: string;
  changedFields: string[] | null;
}

export interface AuditDetailEntry extends AuditListEntry {
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface AuditListResult {
  entries: AuditListEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const auditKeys = {
  all: ['audit'] as const,
  lists: () => [...auditKeys.all, 'list'] as const,
  list: (q: Partial<AuditListQuery>) => [...auditKeys.lists(), q] as const,
  forEntity: (entityType: AuditEntityType, entityId: string, q: Partial<AuditListQuery>) =>
    [...auditKeys.all, 'entity', entityType, entityId, q] as const,
  detail: (id: string) => [...auditKeys.all, 'detail', id] as const,
};

function buildSearch(query: Partial<AuditListQuery>): string {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  if (query.entityType) params.set('entityType', query.entityType);
  if (query.entityId) params.set('entityId', query.entityId);
  if (query.action) params.set('action', query.action);
  if (query.userId) params.set('userId', query.userId);
  if (query.dateFrom) params.set('dateFrom', query.dateFrom);
  if (query.dateTo) params.set('dateTo', query.dateTo);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useAuditList(query: Partial<AuditListQuery> = {}) {
  const fullQuery = { page: query.page ?? 1, limit: query.limit ?? 40, ...query };
  return useQuery({
    queryKey: auditKeys.list(fullQuery),
    queryFn: () => api.get<AuditListResult>(`/audit${buildSearch(fullQuery)}`),
    staleTime: 15_000,
  });
}

export function useAuditForEntity(
  entityType: AuditEntityType,
  entityId: string,
  query: Partial<AuditListQuery> = {},
  enabled = true
) {
  const fullQuery = { page: query.page ?? 1, limit: query.limit ?? 20, ...query };
  return useQuery({
    queryKey: auditKeys.forEntity(entityType, entityId, fullQuery),
    queryFn: () =>
      api.get<AuditListResult>(
        `/audit/entity/${entityType}/${entityId}${buildSearch(fullQuery)}`
      ),
    enabled: enabled && !!entityId,
    staleTime: 15_000,
  });
}

export function useAuditEntry(id: string, enabled = true) {
  return useQuery({
    queryKey: auditKeys.detail(id),
    queryFn: () => api.get<AuditDetailEntry>(`/audit/${id}`),
    enabled: enabled && !!id,
    staleTime: 60_000,
  });
}

// Display helpers

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  create: 'Erstellt',
  update: 'Geändert',
  delete: 'Gelöscht',
};

export const AUDIT_ACTION_COLORS: Record<AuditAction, string> = {
  create: 'bg-green-500/15 text-green-300 border-green-500/20',
  update: 'bg-blue-500/15 text-blue-300 border-blue-500/20',
  delete: 'bg-red-500/15 text-red-300 border-red-500/20',
};

export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  user: 'Benutzer',
  customer: 'Kunde',
  address: 'Adresse',
  invoice: 'Rechnung',
  invoice_item: 'Rechnungsposten',
  payment_reminder: 'Mahnung',
  contract: 'Vertrag',
  contract_item: 'Vertragsposten',
  cost_estimate: 'Kostenvoranschlag',
  estimate_item: 'KV-Posten',
  settings: 'Einstellung',
};

export function auditEntityLabel(entityType: string): string {
  return AUDIT_ENTITY_LABELS[entityType] ?? entityType;
}

export function auditUserLabel(entry: Pick<AuditListEntry, 'userName' | 'userEmail' | 'userId'>): string {
  return entry.userName || entry.userEmail || entry.userId || 'Unbekannt';
}
